import React, { useCallback, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { X, UploadCloud, Loader2, CheckCircle2, AlertTriangle, FileSpreadsheet, FileDown } from 'lucide-react';
import toast from 'react-hot-toast';

export interface ImportFieldDef {
  key: string;
  label: string;
  required?: boolean;
  aliases: string[];
  example?: string;
}

interface ExcelImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  entityLabel: string;
  fieldDefs: ImportFieldDef[];
  importFn: (items: Array<Record<string, any>>) => Promise<{ imported: number; skipped: number; total: number; skippedItems?: any[] }>;
  onSuccess?: () => void;
  templateFilename?: string;
}

const normalizeHeader = (h: string) =>
  String(h || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '')
    .trim();

export const ExcelImportModal: React.FC<ExcelImportModalProps> = ({
  isOpen,
  onClose,
  title = 'Importar dados de Excel',
  entityLabel,
  fieldDefs,
  importFn,
  onSuccess,
  templateFilename,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [rawRows, setRawRows] = useState<Array<Record<string, any>>>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [fileName, setFileName] = useState('');
  const [columnMap, setColumnMap] = useState<Record<string, string>>({});
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ imported: number; skipped: number } | null>(null);

  const autoMapColumns = useCallback(
    (hds: string[]) => {
      const map: Record<string, string> = {};
      const used: string[] = [];
      hds.forEach((header) => {
        const norm = normalizeHeader(header);
        const def = fieldDefs.find(
          (f) =>
            !used.includes(f.key) &&
            (norm === normalizeHeader(f.label) ||
              f.aliases.some((a) => norm === normalizeHeader(a) || norm.includes(normalizeHeader(a))))
        );
        if (def) {
          map[header] = def.key;
          used.push(def.key);
        }
      });
      return map;
    },
    [fieldDefs]
  );

  const handleFile = async (file: File) => {
    if (!file) return;
    setResult(null);
    setParsing(true);
    setFileName(file.name);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      if (!firstSheet) {
        toast.error('Não foi possível ler o ficheiro. Verifique se é um Excel válido.');
        return;
      }
      const rows = XLSX.utils.sheet_to_json<Record<string, any>>(firstSheet, { defval: '' });
      if (rows.length === 0) {
        toast.error('O ficheiro não contém linhas de dados.');
        return;
      }
      const hds = Object.keys(rows[0]);
      setHeaders(hds);
      setRawRows(rows);
      setColumnMap(autoMapColumns(hds));
    } catch (err) {
      console.error('Erro ao ler Excel:', err);
      toast.error('Não foi possível ler o ficheiro. Tente .xlsx, .xls ou .csv.');
    } finally {
      setParsing(false);
    }
  };

  const reset = () => {
    setRawRows([]);
    setHeaders([]);
    setColumnMap({});
    setFileName('');
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDownloadTemplate = () => {
    const header = fieldDefs.map((f) => f.label);
    const example = fieldDefs.map((f) => f.example ?? '');
    const ws = XLSX.utils.aoa_to_sheet([header, example]);
    ws['!cols'] = fieldDefs.map((f) => ({ wch: Math.max(f.label.length + 2, 16) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, templateFilename || `template-${entityLabel}.xlsx`);
    toast.success('Template exemplar descarregado.');
  };

  const mappedCount = headers.filter((h) => columnMap[h]).length;

  const buildItems = () => {
    return rawRows.map((row) => {
      const item: Record<string, any> = {};
      headers.forEach((header) => {
        const field = columnMap[header];
        if (field) item[field] = row[header];
      });
      return item;
    });
  };

  const handleImport = async () => {
    const required = fieldDefs.filter((f) => f.required);
    const missing = required.filter((f) => !Object.values(columnMap).includes(f.key));
    if (missing.length > 0) {
      toast.error(
        `Mapeie as colunas obrigatórias: ${missing.map((m) => m.label).join(', ')}`
      );
      return;
    }

    const items = buildItems();
    const validItems = items.filter((it) =>
      fieldDefs.filter((f) => f.required).every((f) => String(it[f.key] || '').trim() !== '')
    );
    if (validItems.length === 0) {
      toast.error('Nenhuma linha com dados completos para importar.');
      return;
    }

    setImporting(true);
    try {
      const res = await importFn(validItems);
      setResult({ imported: res.imported, skipped: res.skipped });
      toast.success(`${res.imported} ${entityLabel} importado(s) com sucesso.`);
      onSuccess?.();
    } catch (err: any) {
      console.error('Erro ao importar:', err);
      toast.error(err.message || 'Erro ao importar dados.');
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[120] p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-5 sm:px-6 py-4 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <FileSpreadsheet size={22} />
            <h2 className="text-lg sm:text-xl font-bold">{title}</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 sm:p-6 flex flex-col gap-4 overflow-y-auto flex-1">
          {/* Passo 1: escolher ficheiro */}
          {rawRows.length === 0 && (
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files?.[0];
                if (file) handleFile(file);
              }}
              className="border-2 border-dashed border-emerald-300 rounded-xl p-10 flex flex-col items-center gap-3 cursor-pointer hover:bg-emerald-50/50 transition"
            >
              <UploadCloud size={40} className="text-emerald-500" />
              <p className="text-sm font-semibold text-gray-700">
                Clique ou arraste o ficheiro Excel aqui
              </p>
              <p className="text-xs text-gray-400">
                Formatos aceites: .xlsx, .xls, .csv
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                }}
              />
            </div>
          )}

          {rawRows.length === 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-blue-800">
                <FileSpreadsheet size={18} className="shrink-0 text-blue-500" />
                <span>
                  Não sabe o formato? Descarregue o <strong>template exemplar</strong> com as
                  colunas correctas.
                </span>
              </div>
              <button
                onClick={handleDownloadTemplate}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition shrink-0"
              >
                <FileDown size={16} />
                Descarregar Template
              </button>
            </div>
          )}

          {parsing && (
            <div className="flex items-center justify-center gap-2 py-10 text-gray-500">
              <Loader2 size={20} className="animate-spin" />
              A ler ficheiro...
            </div>
          )}

          {/* Passo 2: mapeamento de colunas */}
          {rawRows.length > 0 && !parsing && (
            <>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-800 flex items-center gap-2">
                <CheckCircle2 size={16} className="shrink-0" />
                <span>
                  <strong>{rawRows.length}</strong> linhas lidas de <strong>{fileName}</strong>.
                  Mapeie as colunas abaixo e clique em Importar.
                </span>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Mapeamento de colunas
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    ({mappedCount} de {fieldDefs.length} campos mapeados)
                  </span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {fieldDefs.map((def) => (
                    <label key={def.key} className="flex items-center gap-2 text-sm">
                      <span className="w-32 shrink-0 text-gray-600 truncate">
                        {def.label}
                        {def.required && <span className="text-red-500">*</span>}
                      </span>
                      <select
                        value={Object.keys(columnMap).find((h) => columnMap[h] === def.key) || ''}
                        onChange={(e) => {
                          const newMap: Record<string, string> = {};
                          Object.keys(columnMap).forEach((h) => {
                            if (columnMap[h] !== def.key) newMap[h] = columnMap[h];
                          });
                          if (e.target.value) newMap[e.target.value] = def.key;
                          setColumnMap(newMap);
                        }}
                        className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none bg-white text-sm"
                      >
                        <option value="">— não mapear —</option>
                        {headers.map((h) => (
                          <option key={h} value={h}>
                            {h}
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>
              </div>

              {/* Pré-visualização */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Pré-visualização</h3>
                <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-56">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        {fieldDefs
                          .filter((f) => Object.values(columnMap).includes(f.key))
                          .map((f) => (
                            <th key={f.key} className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">
                              {f.label}
                            </th>
                          ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {buildItems()
                        .slice(0, 6)
                        .map((row, idx) => (
                          <tr key={idx}>
                            {fieldDefs
                              .filter((f) => Object.values(columnMap).includes(f.key))
                              .map((f) => (
                                <td key={f.key} className="px-3 py-2 text-gray-600 max-w-[180px] truncate">
                                  {String(row[f.key] ?? '')}
                                </td>
                              ))}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                {buildItems().length > 6 && (
                  <p className="text-xs text-gray-400 mt-1">
                    ...e mais {buildItems().length - 6} linhas
                  </p>
                )}
              </div>

              {/* Resultado */}
              {result && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 flex items-center gap-3 text-sm">
                  <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
                  <span className="text-gray-700">
                    <strong>{result.imported}</strong> importado(s)
                    {result.skipped > 0 && (
                      <>
                        {' · '}
                        <span className="inline-flex items-center gap-1 text-amber-600">
                          <AlertTriangle size={14} /> {result.skipped} ignorado(s) (já existentes)
                        </span>
                      </>
                    )}
                  </span>
                </div>
              )}

              <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                <button
                  onClick={handleClose}
                  disabled={importing}
                  className="w-full sm:w-1/2 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition"
                >
                  Fechar
                </button>
                <button
                  onClick={handleImport}
                  disabled={importing || mappedCount === 0}
                  className="w-full sm:w-1/2 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition flex items-center justify-center gap-2 disabled:opacity-60 shadow-sm"
                >
                  {importing ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> A importar...
                    </>
                  ) : (
                    'Importar dados'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
